import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Tags as TagsIcon, Plus, Search, Trash2 } from "lucide-react";
import { fetchWithAuth } from "@/lib/api";

interface Tag {
  id: string;
  name: string;
  color: string;
  description: string | null;
  is_client_facing: boolean;
  should_repeat: boolean;
  permanent: boolean;
  usage_count: number;
  created_at: string;
}

export default function Tags() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    color: "#6366F1",
    description: "",
    is_client_facing: false,
    should_repeat: false,
    permanent: false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["tags", search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      return fetchWithAuth<Tag[]>(`/api/tags?${params}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => fetchWithAuth<Tag>("/api/tags", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({ title: "Success", description: "Tag created successfully" });
      closeModal();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create tag", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) =>
      fetchWithAuth<Tag>(`/api/tags/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({ title: "Success", description: "Tag updated successfully" });
      closeModal();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update tag", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fetchWithAuth(`/api/tags/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast({ title: "Success", description: "Tag deleted successfully" });
      closeModal();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({
      name: "",
      color: "#6366F1",
      description: "",
      is_client_facing: false,
      should_repeat: false,
      permanent: false,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (tag: Tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color || "#6366F1",
      description: tag.description || "",
      is_client_facing: tag.is_client_facing,
      should_repeat: tag.should_repeat,
      permanent: tag.permanent,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTag(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTag) {
      updateMutation.mutate({ id: editingTag.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = () => {
    if (editingTag && !editingTag.permanent) {
      deleteMutation.mutate(editingTag.id);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TagsIcon className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="text-page-title">Tags</h1>
              <p className="text-muted-foreground">Manage tags for accounts and projects</p>
            </div>
          </div>
          <Button onClick={openCreateModal} data-testid="button-new-tag">
            <Plus className="w-4 h-4 mr-2" />
            New Tag
          </Button>
        </div>

        <div className="flex items-center gap-4 max-w-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tags..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="input-search-tags"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Client Facing</TableHead>
                <TableHead>Should Repeat</TableHead>
                <TableHead>Usage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Loading tags...
                  </TableCell>
                </TableRow>
              ) : tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No tags found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow
                    key={tag.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openEditModal(tag)}
                    data-testid={`row-tag-${tag.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full border"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="font-medium">{tag.name}</span>
                        {tag.permanent && (
                          <span className="text-xs bg-muted px-1.5 py-0.5 rounded">Permanent</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{tag.color}</code>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {tag.description || "-"}
                    </TableCell>
                    <TableCell>{tag.is_client_facing ? "Yes" : "-"}</TableCell>
                    <TableCell>{tag.should_repeat ? "Yes" : "-"}</TableCell>
                    <TableCell>{tag.usage_count}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? "Edit Tag" : "Create Tag"}</DialogTitle>
            <DialogDescription>
              {editingTag ? "Update tag details" : "Create a new tag for organizing accounts and projects"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg flex items-center justify-center gap-2">
              <div
                className="w-5 h-5 rounded-full border"
                style={{ backgroundColor: formData.color }}
              />
              <span
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: formData.color }}
              >
                {formData.name || "Tag Preview"}
              </span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter tag name"
                required
                data-testid="input-tag-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-10 h-10 rounded border cursor-pointer"
                  data-testid="input-tag-color"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#6366F1"
                  className="font-mono"
                  data-testid="input-tag-color-hex"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
                data-testid="input-tag-description"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is_client_facing">Client Facing</Label>
                  <p className="text-xs text-muted-foreground">Visible to clients in portal</p>
                </div>
                <Switch
                  id="is_client_facing"
                  checked={formData.is_client_facing}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_client_facing: checked })}
                  data-testid="switch-client-facing"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="should_repeat">Should Repeat</Label>
                  <p className="text-xs text-muted-foreground">Persists when project recurs</p>
                </div>
                <Switch
                  id="should_repeat"
                  checked={formData.should_repeat}
                  onCheckedChange={(checked) => setFormData({ ...formData, should_repeat: checked })}
                  data-testid="switch-should-repeat"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="permanent">Permanent</Label>
                  <p className="text-xs text-muted-foreground">Cannot be deleted</p>
                </div>
                <Switch
                  id="permanent"
                  checked={formData.permanent}
                  onCheckedChange={(checked) => setFormData({ ...formData, permanent: checked })}
                  data-testid="switch-permanent"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              {editingTag && !editingTag.permanent ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  data-testid="button-delete-tag"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeModal} data-testid="button-cancel">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="button-save-tag">
                  {isSubmitting ? "Saving..." : editingTag ? "Save Changes" : "Create Tag"}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
